import { useState, useEffect } from "react";
import {
  isReadOnlyUser,
} from "../../../shared/utility";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import ReceivingSubTable1 from "./subtables/ReceivingSubtable1";
import TableLoadingSkeleton from "../../shared/TableLoadingSkeleton";
import Button from "../../controls/Button";
import NumberInputText from "../../controls/inputs/NumberInputText";
import { useCellSaveState } from "../../../hooks/useCellSaveState";
import CopyableIdentifier from "../../controls/CopyableIdentifier";

export default function ReceivingItemsTableComponent(props) {
  const {
    loading,
    values,
    askReturnSelectedItems,
    selectedItems,
    setSelectedItems,
    onUpdateUnitCostForReceivingItem,
    onUpdateTotalCostForReceivingItem,
    onUpdateGroupQuantities,
    updateReceivingItem,
    closed,
    loadingRows,
    rowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    fetchRowDetails,
  } = props;

  const lotNumberBody = (item) => (
    <CopyableIdentifier value={item.lot_number} description="lot number" />
  );

  // optional pagination props from parent
  const { onFetchPage, rowsPerPage: rowsPerPageProp } = props;
  const [currentPage, setCurrentPage] = useState(1);

  const [expandedRows, setExpandedRows] = useState(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(rowsPerPageProp || 15);
  const { getSaveStatus, saveCell } = useCellSaveState();

  const parentCellKey = (item, field) => `parent:${item.id}:${field}`;

  const allowExpansion = (rowData) => {
    return true;
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.unique_id]) {
      fetchRowDetails(rowData);
    }
  };

  const rowExpansionTemplate = (data) => {
    return (
      <ReceivingSubTable1
        data={data}
        loadingRows={loadingRows}
        rowDetails={rowDetails}
        fetchSubRowDetails={fetchSubRowDetails}
        subRowDetails={subRowDetails}
        subLoadingRows={subLoadingRows}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        closed={closed}
        onUpdateReceivingItem={(payload, parentRow) =>
          updateReceivingItem({
            ...payload,
            parentRow,
            is_paginated: isPaginated,
            page: currentPage,
          })
        }
      />
    );
  };

  // Determine whether items is paginated (Laravel LengthAwarePaginator shape)
  const isPaginated =
    values &&
    values.items &&
    typeof values.items === "object" &&
    !Array.isArray(values.items) &&
    Object.prototype.hasOwnProperty.call(values.items, "data") &&
    Object.prototype.hasOwnProperty.call(values.items, "current_page");

  const items = isPaginated
    ? values.items.data
    : Array.isArray(values.items)
    ? values.items
    : [];
  const perPage = isPaginated ? values.items.per_page : rowsPerPageProp || rows;
  const totalRecords = isPaginated ? values.items.total : items.length;

  // keep rows/perPage in sync when values change
  useEffect(() => {
    if (perPage && perPage !== rows) {
      setRows(perPage);
    }
    // rows intentionally excluded to avoid loop; setRows is safe here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage]);

  // sync `first` offset from incoming paginator (so UI stays in sync when parent updates)
  const itemsPagin = values && values.items ? values.items : null;
  useEffect(() => {
    if (isPaginated && itemsPagin) {
      const newFirst =
        (itemsPagin.current_page - 1) * (itemsPagin.per_page || rows);
      setFirst(newFirst);
    } else {
      setFirst(0);
    }
    // rows intentionally excluded to avoid loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginated, itemsPagin]);

  const bodyProductName = (item) => {
    return (
      <p>
        {item.product_name} <br /> {item.plu}
      </p>
    );
  };

  const bodyParentTotalWeight = (item) => {
    return (
      <NumberInputText
        value={item.total_weight}
        aria-label="Total weight"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={!canEdit || typeof onUpdateGroupQuantities !== "function"}
        saveStatus={getSaveStatus(parentCellKey(item, "total_weight"))}
        onCommit={(value) =>
          saveCell(parentCellKey(item, "total_weight"), () =>
            updateGroupQuantity(item, "total_weight", value),
          )
        }
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
        suffix={` ${item.weight_stock_unit_of_measurement_name}`}
      />
    );
  };

  const bodyParentItemWeight = (item) => {
    return (
      <NumberInputText
        value={item.net_weight}
        aria-label="Unit weight"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={!canEdit || typeof onUpdateGroupQuantities !== "function"}
        saveStatus={getSaveStatus(parentCellKey(item, "unit_weight"))}
        onCommit={(value) =>
          saveCell(parentCellKey(item, "unit_weight"), () =>
            updateGroupQuantity(item, "unit_weight", value),
          )
        }
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
        suffix={` ${item.weight_stock_unit_of_measurement_name}`}
      />
    );
  };

  const bodyParentPieceCount = (item) => {
    return (
      <NumberInputText
        value={item.total_pieces}
        aria-label="Total pieces"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={!canEdit || typeof onUpdateGroupQuantities !== "function"}
        saveStatus={getSaveStatus(parentCellKey(item, "total_pieces"))}
        onCommit={(value) =>
          saveCell(parentCellKey(item, "total_pieces"), () =>
            updateGroupQuantity(item, "total_pieces", value),
          )
        }
        minFractionDigits={0}
        maxFractionDigits={0}
        min={0}
        suffix=" pieces"
      />
    );
  };

  const bodyParentUnitCost = (item) => {
    return (
      <NumberInputText
        value={item.unit_cost}
        aria-label="Unit cost"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={!canEdit || typeof onUpdateUnitCostForReceivingItem !== "function"}
        saveStatus={getSaveStatus(parentCellKey(item, "unit_cost"))}
        onCommit={(unitCost) =>
          saveCell(parentCellKey(item, "unit_cost"), () =>
            updateParentValue(item, () =>
              onUpdateUnitCostForReceivingItem({
                unique_id: item.unique_id,
                plu: item.plu,
                lot_number: item.lot_number,
                unit_cost: unitCost,
                warehouse_location_id: item.warehouse_location_id,
                is_paginated: isPaginated,
                page: currentPage,
              }),
            ),
          )
        }
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
      />
    );
  };

  const bodyParentTotalCost = (item) => {
    return (
      <NumberInputText
        value={item.total_cost}
        aria-label="Total cost"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={!canEdit || typeof onUpdateTotalCostForReceivingItem !== "function"}
        saveStatus={getSaveStatus(parentCellKey(item, "total_cost"))}
        onCommit={(totalCost) =>
          saveCell(parentCellKey(item, "total_cost"), () =>
            updateParentValue(item, () =>
              onUpdateTotalCostForReceivingItem({
                unique_id: item.unique_id,
                plu: item.plu,
                lot_number: item.lot_number,
                total_cost: totalCost,
                warehouse_location_id: item.warehouse_location_id,
                total_weight: item.total_weight,
                total_pieces: item.total_pieces,
                stock_unit_of_measurement: item.stock_unit_of_measurement,
              }),
            ),
          )
        }
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
      />
    );
  };

  const bodyParentLocation = (item) => {
    return (
      <p>
        {item.warehouse_name} <br />
        {item.warehouse_location_name}
      </p>
    );
  };

  const cartonCountBody = (item) => {
    return (
      <NumberInputText
        value={item.carton_count}
        aria-label="Carton count"
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled
        minFractionDigits={0}
        maxFractionDigits={0}
        suffix=" cartons"
      />
    );
  };

  const isReadOnly = isReadOnlyUser();
  const canEdit = !isReadOnly && !closed;

  const updateParentValue = async (item, update) => {
    const result = await update();
    if (result?.success && expandedRows?.[item.id]) {
      await fetchRowDetails(item);
    }
    return result;
  };

  const updateGroupQuantity = (item, field, value) =>
    updateParentValue(item, () =>
      onUpdateGroupQuantities({
        product_id: item.product_id,
        lot_number: item.lot_number,
        warehouse_location_id: item.warehouse_location_id,
        field,
        value,
        is_paginated: isPaginated,
        page: currentPage,
      }),
    );

  return (
    <div className="flow-root">
      <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 space-y-4">
          <div className="w-full text-right">
            {!isReadOnly && (
              <Button
                variant="danger"
                className="ml-2 px-1.5 py-1.5"
                onClick={askReturnSelectedItems}
              >
                Delete
              </Button>
            )}
          </div>
          {loading ? (
            <TableLoadingSkeleton rows={5} columns={4} />
          ) : (
            <div className="overflow-hidden card-custom bg-white">
              <DataTable
                value={items}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                }}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
                tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                cellClassName="p-2 border-1-bottom-gray text-sm"
              >
                <Column
                  header="Product Name"
                  body={bodyProductName}
                  style={{ width: "25%" }}
                />
                <Column
                  header="Lot Number"
                  body={lotNumberBody}
                  style={{ width: "15%" }}
                />
                <Column
                  header="Carton Count"
                  body={cartonCountBody}
                  style={{ width: "10%" }}
                />
                <Column
                  header="Unit Wt"
                  body={bodyParentItemWeight}
                  style={{ width: "5%" }}
                />
                <Column
                  header="Total Wt"
                  body={bodyParentTotalWeight}
                  style={{ width: "5%" }}
                />
                <Column
                  header="Total Pieces"
                  body={bodyParentPieceCount}
                  style={{ width: "8%" }}
                />
                <Column
                  field="unit_cost"
                  header="Unit Cost"
                  body={bodyParentUnitCost}
                  style={{ width: "8%" }}
                />
                <Column
                  field="total_cost"
                  header="Total Cost"
                  body={bodyParentTotalCost}
                  style={{ width: "8%" }}
                />
                <Column
                  body={bodyParentLocation}
                  header="Location"
                  style={{ width: "15%" }}
                />
                <Column expander={allowExpansion} style={{ width: "6%" }} />
              </DataTable>
              {isPaginated ? (
                <div className="w-full">
                  <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPageChange={(event) => {
                      const nextPage = event.page + 1;
                      setFirst(event.first);
                      setRows(event.rows);
                      setCurrentPage(nextPage);
                      if (onFetchPage) onFetchPage(nextPage);
                    }}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
